import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Bell,
  Bug,
  Send,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Mail,
  Loader2,
  Timer,
  RefreshCw,
  ClipboardCheck,
  ListTodo,
  ShieldAlert,
  ExternalLink,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { cn, formatDateTime } from '../lib/utils';

const TYPE_CONFIG = {
  incident_24h: { label: 'Incidente 24h', icon: Clock, color: 'text-red-600', bg: 'bg-red-100', linkPath: (n) => `/incidents` },
  incident_72h: { label: 'Incidente 72h', icon: Clock, color: 'text-orange-600', bg: 'bg-orange-100', linkPath: (n) => `/incidents` },
  incident_created: { label: 'Incidente criado', icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-100', linkPath: () => '/incidents' },
  vuln_critical: { label: 'Vulnerabilidade Crítica', icon: Bug, color: 'text-red-600', bg: 'bg-red-100', linkPath: (n) => `/vulnerabilities?focus=${n.entity_id}` },
  task_due: { label: 'Tarefa em atraso', icon: ListTodo, color: 'text-blue-600', bg: 'bg-blue-100', linkPath: () => '/tasks' },
  approval_request: { label: 'Pedido de Aprovação', icon: ClipboardCheck, color: 'text-purple-600', bg: 'bg-purple-100', linkPath: () => '/approvals' },
};

const getTypeCfg = (type) => TYPE_CONFIG[type] || { label: type || 'Notificação', icon: Bell, color: 'text-slate-600', bg: 'bg-slate-100', linkPath: () => null };

export default function Notifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [pendingAlerts, setPendingAlerts] = useState({ alerts_24h: [], alerts_72h: [], total_pending: 0 });
  const [stats, setStats] = useState({ total: 0, sent: 0, failed: 0, pending: 0 });
  const [typeFilter, setTypeFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [selectedAlertType, setSelectedAlertType] = useState('24h');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [notificationsRes, pendingRes, statsRes] = await Promise.all([
        api.get('/notifications?limit=100'),
        api.get('/notifications/pending-alerts'),
        api.get('/notifications/stats')
      ]);
      
      setNotifications(notificationsRes.data);
      setPendingAlerts(pendingRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar notificações');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendAlert = async () => {
    if (!recipientEmail || !selectedIncident) {
      toast.error('Email do destinatário é obrigatório');
      return;
    }

    setIsSending(true);
    try {
      const response = await api.post('/notifications/send-incident-alert', {
        incident_id: selectedIncident.incident_id,
        alert_type: selectedAlertType,
        recipient_email: recipientEmail,
        recipient_name: recipientName || undefined
      });
      
      if (response.data.status === 'failed') {
        toast.warning(`Notificação registada, mas envio falhou: ${response.data.email_result?.error || 'Erro desconhecido'}`);
      } else {
        toast.success('Notificação enviada com sucesso!');
      }
      
      setShowSendDialog(false);
      setSelectedIncident(null);
      setRecipientEmail('');
      setRecipientName('');
      loadData();
    } catch (error) {
      toast.error('Erro ao enviar notificação');
    } finally {
      setIsSending(false);
    }
  };

  const openSendDialog = (incident, alertType) => {
    setSelectedIncident(incident);
    setSelectedAlertType(alertType);
    setShowSendDialog(true);
  };

  const getStatusBadge = (status) => {
    const configs = {
      sent: { label: 'Enviado', className: 'bg-green-50 text-green-700 border-green-200' },
      failed: { label: 'Falhou', className: 'bg-red-50 text-red-700 border-red-200' },
      pending: { label: 'Pendente', className: 'bg-yellow-50 text-yellow-700 border-yellow-200' }
    };
    const config = configs[status] || configs.pending;
    return <Badge variant="outline" className={cn('text-xs', config.className)}>{config.label}</Badge>;
  };

  const headerContent = (
    <Button variant="outline" onClick={loadData} data-testid="refresh-notifications">
      <RefreshCw className="h-4 w-4 mr-2" />
      Atualizar
    </Button>
  );

  if (isLoading) {
    return (
      <Layout title="Notificações NIS2">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Notificações NIS2" headerContent={headerContent}>
      <div className="space-y-6" data-testid="notifications-container">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="card-flat">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Bell className="h-8 w-8 text-slate-400" />
                <span className="text-2xl font-bold text-slate-900">{stats.total}</span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Total</p>
            </CardContent>
          </Card>
          <Card className="card-flat border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <CheckCircle className="h-8 w-8 text-green-500" />
                <span className="text-2xl font-bold text-green-600">{stats.sent}</span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Enviadas</p>
            </CardContent>
          </Card>
          <Card className="card-flat border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <XCircle className="h-8 w-8 text-red-500" />
                <span className="text-2xl font-bold text-red-600">{stats.failed}</span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Falhadas</p>
            </CardContent>
          </Card>
          <Card className="card-flat border-l-4 border-l-orange-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Timer className="h-8 w-8 text-orange-500" />
                <span className="text-2xl font-bold text-orange-600">{pendingAlerts.total_pending}</span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Alertas Pendentes</p>
            </CardContent>
          </Card>
        </div>

        {/* Pending Alerts */}
        {(pendingAlerts.alerts_24h.length > 0 || pendingAlerts.alerts_72h.length > 0) && (
          <Card className="card-flat border-l-4 border-l-red-500">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                Alertas NIS2 Pendentes
              </CardTitle>
              <CardDescription>
                Incidentes que requerem notificação à autoridade competente
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {pendingAlerts.alerts_24h.length > 0 && (
                <div>
                  <h4 className="font-medium text-red-700 mb-2 flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Alertas 24 Horas ({pendingAlerts.alerts_24h.length})
                  </h4>
                  <div className="space-y-2">
                    {pendingAlerts.alerts_24h.map((alert) => (
                      <div key={alert.incident_id} className="flex items-center justify-between bg-red-50 p-3 rounded-lg">
                        <div>
                          <span className="font-mono text-xs text-slate-500">{alert.incident_id}</span>
                          <p className="font-medium text-slate-900">{alert.title}</p>
                          <p className="text-sm text-slate-500">{alert.hours_elapsed}h desde criação</p>
                        </div>
                        <Button size="sm" onClick={() => openSendDialog(alert, '24h')}>
                          <Send className="h-4 w-4 mr-1" />
                          Notificar
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {pendingAlerts.alerts_72h.length > 0 && (
                <div>
                  <h4 className="font-medium text-orange-700 mb-2 flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Alertas 72 Horas ({pendingAlerts.alerts_72h.length})
                  </h4>
                  <div className="space-y-2">
                    {pendingAlerts.alerts_72h.map((alert) => (
                      <div key={alert.incident_id} className="flex items-center justify-between bg-orange-50 p-3 rounded-lg">
                        <div>
                          <span className="font-mono text-xs text-slate-500">{alert.incident_id}</span>
                          <p className="font-medium text-slate-900">{alert.title}</p>
                          <p className="text-sm text-slate-500">{alert.hours_elapsed}h desde criação</p>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => openSendDialog(alert, '72h')}>
                          <Send className="h-4 w-4 mr-1" />
                          Notificar
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* NIS2 Info Card */}
        <Card className="card-flat bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Bell className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900">Prazos de Notificação NIS2</h4>
                <ul className="text-sm text-blue-700 mt-2 space-y-1">
                  <li><strong>24 horas:</strong> Alerta inicial à autoridade competente (CNCS)</li>
                  <li><strong>72 horas:</strong> Relatório completo do incidente</li>
                  <li><strong>1 mês:</strong> Relatório final (após resolução)</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notification History */}
        <Card className="card-flat">
          <CardHeader>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <CardTitle className="text-lg">Histórico de Notificações</CardTitle>
              <div className="flex gap-2 flex-wrap" data-testid="notif-type-filters">
                {[
                  { value: 'all', label: 'Todas' },
                  { value: 'vuln_critical', label: 'Vulns Críticas' },
                  { value: 'incident_24h', label: 'Incidente 24h' },
                  { value: 'incident_72h', label: 'Incidente 72h' },
                  { value: 'task_due', label: 'Tarefas' },
                  { value: 'approval_request', label: 'Aprovações' },
                ].map(t => (
                  <Button
                    key={t.value}
                    variant={typeFilter === t.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTypeFilter(t.value)}
                    data-testid={`notif-filter-${t.value}`}
                  >
                    {t.label}
                  </Button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {(() => {
              const visible = typeFilter === 'all'
                ? notifications
                : notifications.filter(n => n.notification_type === typeFilter);

              if (visible.length === 0) {
                return (
                  <div className="text-center py-8 text-slate-500" data-testid="no-notifications-msg">
                    <Mail className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>Nenhuma notificação {typeFilter === 'all' ? 'enviada' : 'deste tipo'}</p>
                  </div>
                );
              }
              return (
                <div className="space-y-3" data-testid="notifications-list">
                  {visible.map((notif) => {
                    const cfg = getTypeCfg(notif.notification_type);
                    const Icon = cfg.icon;
                    const linkTo = cfg.linkPath(notif);
                    return (
                      <div
                        key={notif.notification_id}
                        className={cn(
                          'flex items-center justify-between p-3 bg-slate-50 rounded-lg gap-3',
                          linkTo && 'cursor-pointer hover:bg-slate-100 transition-colors'
                        )}
                        onClick={() => linkTo && navigate(linkTo)}
                        data-testid={`notif-${notif.notification_id}`}
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className={cn('h-10 w-10 rounded-lg flex items-center justify-center shrink-0', cfg.bg)}>
                            <Icon className={cn('h-5 w-5', cfg.color)} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <Badge variant="outline" className="text-xs" data-testid={`notif-type-${notif.notification_id}`}>
                                {cfg.label}
                              </Badge>
                              {notif.metadata?.cvss_score != null && (
                                <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                                  CVSS {notif.metadata.cvss_score}
                                </Badge>
                              )}
                              {notif.metadata?.cve_id && (
                                <Badge variant="outline" className="text-xs font-mono bg-slate-100">
                                  {notif.metadata.cve_id}
                                </Badge>
                              )}
                            </div>
                            <p className="font-medium text-slate-900 truncate">{notif.subject}</p>
                            <p className="text-xs text-slate-500 truncate">
                              Para: {notif.recipient_email} • {formatDateTime(notif.created_at)}
                              {notif.metadata?.asset_name && ` • Ativo: ${notif.metadata.asset_name}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {getStatusBadge(notif.status)}
                          {linkTo && <ExternalLink className="h-4 w-4 text-slate-400" />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      </div>

      {/* Send Alert Dialog */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enviar Notificação NIS2</DialogTitle>
            <DialogDescription>
              Enviar alerta de {selectedAlertType} para o incidente {selectedIncident?.incident_id}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="font-medium text-slate-900">{selectedIncident?.title}</p>
              <p className="text-sm text-slate-500">Severidade: {selectedIncident?.severity}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email do Destinatário *</Label>
              <Input
                id="email"
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="ciso@empresa.pt"
                data-testid="notification-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Nome do Destinatário</Label>
              <Input
                id="name"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                placeholder="João Silva"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendDialog(false)}>Cancelar</Button>
            <Button onClick={handleSendAlert} disabled={isSending} data-testid="send-notification-btn">
              {isSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Send className="mr-2 h-4 w-4" />
              Enviar Notificação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
